import * as React from "react"

import { Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { formatMoneySafe } from "@/features/finance/overview/lib/overview-format"
import { useGlobalBranchRows } from "@/features/finance/overview/lib/use-global-branch-rows"

/**
 * Branches that spent more than they received this period.
 *
 * A consolidated net can be comfortably positive while individual branches are running down their cash, because
 * the strong ones net out the weak ones. That is precisely what a workspace-level total hides, and it is the
 * thing worth acting on early — a branch in deficit for a second period is a conversation, not a number.
 *
 * GLOBAL only, and deliberately silent when nothing is wrong: an empty list here means every branch covered its
 * own costs.
 */
export function BranchDeficitWidget() {
  const t = useTranslations("finance.overview")
  const { rows, currency, isLoading, isError, isFetching, refetch } = useGlobalBranchRows()

  const data = React.useMemo(() => {
    // Sorted by depth of deficit: the backend sorts by net descending, so the worst are last.
    const deficits = rows.filter((row) => row.netIsNegative).slice().reverse()
    return { deficits, currency, total: rows.length }
  }, [rows, currency])

  useWidgetMenu({ refresh: refetch })

  return (
    <WidgetBoundary
      isLoading={isLoading}
      isError={isError}
      isFetching={isFetching}
      data={data}
      skeleton={<DeficitSkeleton />}
      errorTitle={t("widgets.branchDeficit.error")}
      onRetry={refetch}
      retryLabel={t("widgets.common.retry")}
    >
      {(d) =>
        d.deficits.length === 0 ? (
          <div className='space-y-1'>
            <p className='text-2xl font-semibold tracking-tight text-emerald-600 dark:text-emerald-400'>
              {t("widgets.branchDeficit.none.value")}
            </p>
            <p className='text-muted-foreground text-xs'>{t("widgets.branchDeficit.none.description")}</p>
          </div>
        ) : (
          <div className='space-y-3'>
            <div className='flex items-end justify-between gap-2'>
              <p className='text-destructive text-2xl font-semibold tabular-nums tracking-tight'>
                {d.deficits.length}
              </p>
              <p className='text-muted-foreground text-xs'>
                {t("widgets.branchDeficit.ratio", { deficit: d.deficits.length, total: d.total })}
              </p>
            </div>
            <ul className='divide-border/60 divide-y'>
              {d.deficits.map((row) => (
                <li key={row.id} className='flex items-center justify-between gap-2 py-1.5'>
                  <span className='text-foreground min-w-0 truncate text-sm'>{row.name}</span>
                  <span className='text-destructive shrink-0 text-sm font-medium tabular-nums'>
                    {formatMoneySafe(row.net, d.currency)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )
      }
    </WidgetBoundary>
  )
}

function DeficitSkeleton() {
  return (
    <div className='space-y-3'>
      <Skeleton className='h-7 w-12' />
      {[0, 1].map((row) => (
        <div key={row} className='flex items-center justify-between py-1'>
          <Skeleton className='h-3 w-28' />
          <Skeleton className='h-3.5 w-16' />
        </div>
      ))}
    </div>
  )
}
